import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, RevealLines } from "@/components/motion";
import { CONCEPTS } from "@/data/content";

export const ConceptGallery = () => {
  const navigate = useNavigate();
  return (
    <section id="concepts" data-testid="concept-gallery">
      <div className="bg-[#FDFBF7] pt-28 md:pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                — Concept Gallery
              </p>
              <h2 className="font-serif font-light text-5xl md:text-6xl leading-[0.98] tracking-tight">
                <RevealLines lines={["Ideas we've explored,", "not yet commissioned."]} />
              </h2>
              <p className="text-base md:text-lg text-[#1A1713]/70 mt-8 leading-relaxed">
                Each concept below is a self-directed exploration — a way of
                showing how we think before a brief exists. None of these are
                client engagements.
              </p>
            </div>
            <button
              onClick={() => navigate("/concepts")}
              data-testid="view-all-concepts"
              className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] whitespace-nowrap"
            >
              View gallery &amp; concept lab
              <ArrowUpRight size={16} className="text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-6 md:gap-8">
        {CONCEPTS.map((c, i) => (
          <Reveal key={c.slug} i={i}>
            <button
              onClick={() => navigate("/concepts")}
              data-testid={`concept-card-${c.slug}`}
              className="group text-left w-full"
            >
              <div className="relative overflow-hidden h-[300px] mb-6">
                <motion.img
                  src={c.img}
                  alt={c.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.06 }}
                  transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
                />
              </div>
              <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-[#8a6d2f] bg-[#C5A059]/15 px-3 py-1 rounded-full">
                {c.tag}
              </span>
              <h3 className="font-serif text-3xl mt-4 leading-tight">{c.title}</h3>
              <p className="text-sm text-[#78716C] mt-2 leading-relaxed">{c.desc}</p>
            </button>
          </Reveal>
        ))}
      </div>

      <div className="bg-[#1A1713] text-[#FDFBF7] mt-20 py-24 md:py-28">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <h3 className="font-serif font-light text-4xl md:text-6xl tracking-tight leading-none">
              Curious what <em className="italic text-[#C5A059]">yours</em> would look like?
            </h3>
            <p className="text-base md:text-lg text-[#FDFBF7]/60 mt-6 max-w-xl mx-auto">
              Tell us about your establishment and we&rsquo;ll sketch a concept
              direction — free, no obligation.
            </p>
            <button
              onClick={() => navigate("/request-concept")}
              data-testid="request-concept-cta"
              className="group mt-10 inline-flex items-center gap-3 bg-[#C5A059] text-[#1A1713] px-10 py-5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#FDFBF7] transition-colors duration-400"
            >
              Request a concept for your brand
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
